// Rastreo de un envio por paqueteria · va dentro de la tarjeta de la guia (EnvioPaqueteria).
//
// 4-nov-2026 (dueño): la PWA debe decir si el paquete ya se entregó o si sigue En Tránsito, con lo
// que reporta Skydropx. El servidor pregunta solo cada 2 h (src/services/rastreoEnvios.js); aquí
// se ve el estado, los movimientos y un botón para preguntar en ese momento. Solo pantalla: no
// manda avisos, y la app de la clienta y el espejo de la tienda no se tocan.
//
// Al abrir el pedido se actualiza solo si lo guardado ya tiene más de 30 min y el envío no ha
// terminado (entregado, cancelado o destruido ya no cambian). El servidor además no vuelve a
// preguntarle a Skydropx si alguien lo revisó hace menos de un minuto.
import React from 'react';
import {
  etiqueta, esEntregado, esFinal, etiquetaEvento, tonoEvento,
  fechaCorta, fechaHora, horaMx, haceCuanto,
} from './rastreoEstados.js';
const { useState, useEffect, useRef, useCallback } = React;

const CSS = `
.rs{margin-top:12px;padding-top:12px;border-top:1px solid #E2E8F0}
.rs-est{border:1px solid #CBD5E1;background:#F8FAFC;color:#334155;padding:10px 12px}
.rs-est.camino{border-color:#1D4ED8;background:#EFF6FF;color:#1E3A8A}
.rs-est.ok{border-color:#1D8A4E;background:#E6F4EA;color:#14532D}
.rs-est.alerta{border-color:#D97706;background:#FEF3C7;color:#78350F}
.rs-est .rs-t{display:block;font-size:17px;font-weight:600;line-height:1.3;letter-spacing:0}
.rs-est .rs-d{display:block;font-size:12px;margin-top:4px;line-height:1.45}
.rs-mov{list-style:none;margin:10px 0 0;padding:0}
.rs-mov li{padding:7px 0;border-bottom:1px solid #F1F5F9;font-size:12px;color:#475569;line-height:1.45}
.rs-mov li:last-child{border-bottom:0}
.rs-mov .rs-f{color:#64748B;white-space:nowrap}
.rs-mov .rs-e{font-weight:600;color:#0F172A}
.rs-mov .rs-e.alerta{color:#B45309}
.rs-mov .rs-e.ok{color:#14532D}
.rs-mas{display:block;background:none;border:0;padding:4px 0;color:#1D4ED8;font-size:12px;text-decoration:underline;cursor:pointer}
.rs-pie{font-size:11px;color:#94A3B8;margin:8px 0 0;line-height:1.4}
.rs-err{font-size:11px;color:#B91C1C;margin:4px 0 0;line-height:1.4}
.rs .c2-btn{margin-top:8px}
`;

const VIEJO_MS = 30 * 60000;   // más de 30 min guardado: al abrir se vuelve a preguntar
const VISIBLES = 5;

// Mismo token que el resto de la app (como api() de EnviosLista y EnvioPaqueteria).
async function pedir(pedidoId) {
  const tok = localStorage.getItem('token');
  const r = await fetch('/api/envios/' + encodeURIComponent(pedidoId) + '/rastreo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
    body: '{}',
  });
  const j = await r.json().catch(() => null);
  if (!r.ok || !j || j.ok === false) throw new Error((j && (j.error || j.message)) || ('no se pudo (HTTP ' + r.status + ')'));
  return j.rastreo || null;
}

const masNuevo = (a, b) => (Date.parse((a && a.consultado_at) || '') || 0) > (Date.parse((b && b.consultado_at) || '') || 0);

// " · " entre las partes que sí vienen.
const junta = (...p) => p.filter(Boolean).join(' · ');

export default function RastreoEnvio({ pedidoId, envio }) {
  const guia = (envio && envio.guia) || '';
  const paqueteria = (envio && envio.paqueteria) || '';
  const delPadre = (envio && envio.rastreo) || null;
  const [rastreo, setRastreo] = useState(delPadre);
  const [cargando, setCargando] = useState(false);
  const [err, setErr] = useState('');
  const [verTodos, setVerTodos] = useState(false);
  const [ahora, setAhora] = useState(() => Date.now());
  const vivo = useRef(true);
  const yaSolo = useRef(false);

  useEffect(() => {
    vivo.current = true;
    return () => { vivo.current = false; };
  }, []);

  // Si la pantalla de arriba trae un envío más nuevo (p.ej. al guardar lo cobrado), se usa ese.
  useEffect(() => {
    if (delPadre) setRastreo(r => (masNuevo(delPadre, r) ? delPadre : r));
  }, [delPadre]);

  // El "Revisado hace X min" se mueve solo mientras la pantalla está abierta.
  useEffect(() => {
    const t = setInterval(() => setAhora(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const actualizar = useCallback(async () => {
    if (!guia) return;
    setCargando(true); setErr('');
    try {
      const r = await pedir(pedidoId);
      if (!vivo.current) return;
      if (r) setRastreo(r);
      setAhora(Date.now());
    } catch (e) {
      if (vivo.current) setErr('No se pudo actualizar el rastreo: ' + e.message);
    } finally {
      if (vivo.current) setCargando(false);
    }
  }, [pedidoId, guia]);

  // Al abrir: solo si nunca se ha revisado o si lo guardado ya está viejo y todavía puede cambiar.
  // Se decide UNA vez (yaSolo): si después cambia el envío de arriba no se vuelve a preguntar solo.
  useEffect(() => {
    if (yaSolo.current || !guia || !paqueteria) return;
    yaSolo.current = true;
    const consultado = Date.parse((delPadre && delPadre.consultado_at) || '') || 0;
    if (!delPadre || (!esFinal(delPadre) && Date.now() - consultado > VIEJO_MS)) actualizar();
  }, [guia, paqueteria, delPadre, actualizar]);

  if (!guia) return null;

  const et = etiqueta(rastreo);
  const entregado = esEntregado(rastreo);
  const eventos = (rastreo && Array.isArray(rastreo.eventos)) ? rastreo.eventos : [];
  const visibles = verTodos ? eventos : eventos.slice(0, VISIBLES);
  const cuandoEntrego = rastreo && (rastreo.entregado_at || rastreo.fecha);

  let detalle = '';
  if (entregado) {
    detalle = 'Entregado' + (cuandoEntrego ? ' el ' + fechaCorta(cuandoEntrego) + ' a las ' + horaMx(cuandoEntrego) : '') +
      (rastreo.recibio ? ' · recibió ' + rastreo.recibio : '');
  } else if (rastreo && rastreo.estado) {
    const ult = junta(rastreo.descripcion, rastreo.lugar, fechaHora(rastreo.fecha));
    if (ult) detalle = 'Último movimiento: ' + ult;
  } else if (rastreo && rastreo.sin_eventos) {
    detalle = 'La paquetería todavía no reporta nada de esta guía. Suele aparecer cuando recogen el paquete.';
  } else if (!rastreo) {
    detalle = cargando ? 'Preguntando a la paquetería…' : 'Todavía no se ha revisado con la paquetería.';
  }

  return (
    <div className="rs" data-rs="rastreo">
      <style>{CSS}</style>
      <div className={'rs-est ' + et.tono}>
        <span className="rs-t">{entregado ? 'Entregado' : et.texto}</span>
        {detalle && <span className="rs-d">{detalle}</span>}
      </div>

      {eventos.length > 0 && (<>
        <ul className="rs-mov">
          {visibles.map((ev, i) => (
            <li key={(ev.fecha || '') + '|' + i}>
              {ev.fecha && <span className="rs-f">{fechaHora(ev.fecha)} · </span>}
              <span className={'rs-e ' + tonoEvento(ev.estado)}>{etiquetaEvento(ev.estado)}</span>
              {ev.descripcion && <> · {ev.descripcion}</>}
              {ev.lugar && <> · {ev.lugar}</>}
            </li>
          ))}
        </ul>
        {eventos.length > VISIBLES && (
          <button type="button" className="rs-mas" onClick={() => setVerTodos(v => !v)}>
            {verTodos ? 'Ver menos' : 'Ver todos (' + eventos.length + ')'}
          </button>
        )}
      </>)}

      {rastreo && rastreo.error ? (
        <p className="rs-err">
          Falló la última revisión{rastreo.error_at ? ' (' + haceCuanto(rastreo.error_at, ahora) + ')' : ''}: {typeof rastreo.error === 'string' ? rastreo.error.replace(/[.\s]+$/, '') : 'la paquetería no contestó'}.
          {rastreo.estado ? ' Arriba está lo último que se supo.' : ''}
        </p>
      ) : (rastreo && rastreo.consultado_at && <p className="rs-pie">Revisado {haceCuanto(rastreo.consultado_at, ahora)}.</p>)}
      {err && <p className="rs-err" role="status">{err}</p>}

      <button type="button" className="c2-btn ghost" onClick={actualizar} disabled={cargando}>
        {cargando ? 'Revisando…' : 'Actualizar rastreo'}
      </button>
    </div>
  );
}
